import { useEffect, useState } from 'react';

import { useStableCallback } from 'core/hooks/useStableCallback';

import useKeyboard from './useKeyboard';
import useSynthesis from './useSynthesis';

type TStatus = 'none' | 'success' | 'error';
type TState = {
  value: number;
  next: number;
  chosen: string;
  status: TStatus;
  correct: number;
  wrong: number;
};
const DEFAULT_STATE: TState = {
  value: 0,
  next: 0,
  chosen: '',
  status: 'none',
  correct: 0,
  wrong: 0,
};

export default function useNumbers(lang: TLang) {
  const [st, setState] = useState<TState>(DEFAULT_STATE);

  useEffect(() => {
    setState({ ...DEFAULT_STATE, value: rand(), next: rand() });
  }, []);

  const onBackspace = useStableCallback((): void =>
    setState(st => ({
      ...st,
      chosen:
        st.chosen.length > 0
          ? st.chosen.slice(0, st.chosen.length - 1)
          : st.chosen,
    }))
  );

  const onAddSym = useStableCallback((sym: string): void =>
    setState(st => ({
      ...st,
      chosen: st.chosen + sym,
    }))
  );

  useEffect(() => {
    const valueStr = String(st.value);
    const isValid = st.chosen
      .split('')
      .every((sym, idx) => sym === valueStr[idx]);

    if (!isValid) {
      setState(st => ({ ...st, wrong: st.wrong + 1, status: 'error' }));
      setTimeout(() => {
        setState(st => ({ ...st, status: 'none', chosen: '' }));
      }, ANIMATION_DELAY);
    } else if (st.chosen === valueStr) {
      setState(st => ({ ...st, correct: st.correct + 1, status: 'success' }));
      setTimeout(() => {
        setState(st => ({
          ...st,
          status: 'none',
          chosen: '',
          value: st.next,
          next: rand(),
        }));
      }, ANIMATION_DELAY);
    }
  }, [st.chosen, st.value]);

  const { isAudioLoading, replay } = useSynthesis(lang, st.value, st.next);

  useKeyboard(onAddSym, onBackspace, replay, st.status !== 'none');

  return { onBackspace, onAddSym, isAudioLoading, replay, ...st };
}

const ANIMATION_DELAY = 400;

const prev: number[] = [];
const PREV_LIMIT = 10;

const rand = (): number => {
  let n: number;

  do {
    do {
      n = Math.floor(Math.random() * 1000);
    } while (n < 10 || n > 1000);

    n = Math.random() > 0.7 || n < 100 ? n : Math.floor(n / 10);
  } while (
    // Don't allow to reuse previously selected numbers
    prev.includes(n)
  );

  // Keep prev smaller than limit
  prev.push(n);
  if (prev.length > PREV_LIMIT) {
    prev.shift();
  }

  return n;
};
